import { ajv, JSONSchema7WithErrors } from '@cibo/core'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import type { FieldError, FormContextValue } from 'dynamic-uniforms'
import { EffectiveSchemaBridge, FormContext } from 'dynamic-uniforms'
import { cloneDeep, set } from 'lodash'
import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoFields } from './AutoFields'
import { CiboAutoFormContext } from './CiboAutoFormContext'
import { dereferenceSchema } from './dereferenceSchema'
import { createWarningsValidator } from './warningsValidator'

type CiboAutoFormProps = {
  warningSchema?: any
  renderDisplay?: (config: { field: any; itemProps: any }) => ReactNode
  schema: JSONSchema7WithErrors
  model?: any
  onSubmit?: (model: any) => void | Promise<void>
  onChangeModel?: (model: any) => void
  validate?: 'onChange' | 'onSubmit'
  disabled?: boolean
  readOnly?: boolean
  children?: ReactNode
}

export function CiboAutoForm({
  model: externalModel,
  schema,
  warningSchema,
  renderDisplay,
  onSubmit,
  onChangeModel,
  validate = 'onSubmit',
  disabled = false,
  readOnly = false,
  children,
}: CiboAutoFormProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const touch = useCallback((name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }))
  }, [])

  // Create wrapper schema that nests user schema under "value"
  const formSchema = useMemo(() => {
    if (!schema?.type) {
      console.error(
        "The configured schema does not have a 'type' property.\n\n",
        JSON.stringify(schema, null, 2),
        '\n\n'
      )
      throw new Error("Schema must include a 'type' property.")
    }

    return {
      type: 'object' as const,
      properties: { value: { $id: 'schema', ...dereferenceSchema(schema, schema) } },
      required: ['value'] as string[],
    }
  }, [schema])

  const effectiveWarningSchema = warningSchema ?? (schema as any)?.['ui-params']?.warningSchema

  const warningValidator = useMemo(() => {
    return effectiveWarningSchema ? createWarningsValidator(effectiveWarningSchema) : undefined
  }, [effectiveWarningSchema])

  const [bridge, setBridge] = useState(
    () => new EffectiveSchemaBridge(formSchema, externalModel ?? {}, ajv)
  )
  const [model, setModel] = useState<Record<string, unknown>>(() => externalModel ?? {})
  const modelRef = useRef(model)
  modelRef.current = model
  const initialBridge = useRef(bridge)
  const [errors, setErrors] = useState<FieldError[]>(() =>
    validate === 'onChange' ? initialBridge.current.validateEffective(externalModel ?? {}) : []
  )
  const [warnings, setWarnings] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const lastNotifiedModel = useRef<Record<string, unknown> | null>(null)

  // Sync when external model or schema changes (skip if we caused the change)
  useEffect(() => {
    if (externalModel === lastNotifiedModel.current) {
      return
    }
    const m = externalModel ?? {}
    const newBridge = new EffectiveSchemaBridge(formSchema, m, ajv)
    setModel(m)
    setBridge(newBridge)
    if (validate === 'onChange') {
      setErrors(newBridge.validateEffective(m))
    }
  }, [externalModel, formSchema, validate])

  const onChangeModelRef = useRef(onChangeModel)
  onChangeModelRef.current = onChangeModel

  const handleChange = useCallback(
    (name: string, value: unknown) => {
      const next = cloneDeep(modelRef.current)
      set(next, name, value)

      const newBridge = new EffectiveSchemaBridge(formSchema, next, ajv)
      const stripped = newBridge.stripModel(next) as Record<string, unknown>

      // Update ref immediately so back-to-back onChange calls
      // within the same event (e.g. setting a sibling field) see
      // each other's changes before React re-renders.
      modelRef.current = stripped

      setModel(stripped)
      setBridge(newBridge)

      if (validate === 'onChange') {
        setErrors(newBridge.validateEffective(stripped))
      }

      if (warningValidator) {
        // warningValidator runs against the user's unwrapped model (outside the "value" wrapper
        // that CiboAutoForm uses internally), so strip it before validating.
        const rawWarnings = warningValidator((stripped as any).value ?? stripped)
        const prefixed: Record<string, string> = {}
        for (const [key, val] of Object.entries(rawWarnings)) {
          prefixed[`value.${key}`] = val
        }
        setWarnings(prefixed)
      }

      if (onChangeModelRef.current) {
        lastNotifiedModel.current = stripped
        onChangeModelRef.current(stripped)
      }
    },
    [formSchema, validate, warningValidator]
  )

  const handleSubmit = useCallback(() => {
    setSubmitted(true)
    const currentBridge = new EffectiveSchemaBridge(formSchema, model, ajv)
    const stripped = currentBridge.stripModel(model) as Record<string, unknown>
    const validationErrors = currentBridge.validateEffective(stripped)
    setErrors(validationErrors)

    if (validationErrors.length > 0) return

    if (onSubmit) {
      setSubmitting(true)
      const result = onSubmit(stripped)
      if (result && typeof (result as Promise<void>).then === 'function') {
        ;(result as Promise<void>)
          .then(() => setSubmitting(false))
          .catch(() => setSubmitting(false))
      } else {
        setSubmitting(false)
      }
    }
  }, [formSchema, model, onSubmit])

  const contextValue = useMemo<FormContextValue>(
    () => ({
      bridge,
      model,
      onChange: handleChange,
      onSubmit: handleSubmit,
      errors,
      ajv,
      warnings,
      touched,
      touch,
      submitted,
      submitting,
      disabled,
      readOnly,
      showInlineError: true,
      namePrefix: '',
    }),
    [
      bridge,
      model,
      handleChange,
      handleSubmit,
      errors,
      warnings,
      touched,
      touch,
      submitted,
      submitting,
      disabled,
      readOnly,
    ]
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CiboAutoFormContext.Provider value={{ renderDisplay }}>
        <FormContext.Provider value={contextValue}>
          <form
            noValidate
            onSubmit={(e: FormEvent) => {
              e.preventDefault()
              handleSubmit()
            }}
          >
            {children ?? <AutoFields />}
          </form>
        </FormContext.Provider>
      </CiboAutoFormContext.Provider>
    </LocalizationProvider>
  )
}
